import { useMutation, useQueryClient } from '@tanstack/react-query';
import { makeRequest } from 'features/entry-form-v2/data/makeRequest';
import { CreateProject, Project } from 'store/data/projects';

type CreateProjectResponse = {
	project: Project;
	statusCode: string;
	httpStatusCode: number;
	projectActions: {
		canAddCollaborator: boolean;
		canInviteExternalUser: boolean;
	};
};

const projectKeys = {
	all: ['projects'] as const,
	lists: () => [...projectKeys.all, 'list'] as const,
	list: (filters: string) => [...projectKeys.lists(), { filters }] as const,
	details: () => [...projectKeys.all, 'detail'] as const,
	detail: (id: string) => [...projectKeys.details(), id] as const
};

export const useProjectCreation = () => {
	const queryClient = useQueryClient();

	const mutation = useMutation(
		(createProject: CreateProject) => {
			const { codelistEnabled, ...project } = createProject;

			return makeRequest<CreateProjectResponse>({
				service: 'projects',
				method: 'addProject',
				data: {
					codelistEnabled,
					project: project
				}
			});
		},
		{
			onSuccess: data => {
				// Invalidate and refetch projects list
				queryClient.invalidateQueries(projectKeys.lists());

				// Update the project detail in the cache
				if (data.project?.projectId) {
					queryClient.setQueryData(
						projectKeys.detail(data.project.projectId),
						data.project
					);
				}
			}
		}
	);

	return {
		createProject: mutation.mutateAsync,
		isLoading: mutation.isLoading,
		error: mutation.error,
		data: mutation.data?.project
	};
};
